/**
 * Pay as you go credits (pricing v4.2; stored as top-up credits:
 * bonus_credits.source_type = 'topup'). Bought once, in blocks of 100, valid
 * 12 months, at one price for every plan, Free included. They stay valid if
 * the plan changes.
 *
 * Price: $0.49 a credit for the first 500, then 15% off the next 500, 25% off
 * credits 1,001 to 2,500 and 35% off every credit above 2,500. Each band only
 * prices the credits inside it, so buying more never costs less. The total is
 * rounded to whole dollars.
 *
 * While an account holds them every feature of the Pro plan is open to it
 * (pricing v4.3); an audit its own plan does not include is paid from them
 * only. Folders and seats stay with the plan.
 *
 * The first purchase of an account gets TOPUP_FIRST_BONUS_PCT more credits,
 * once (grant_topup_credits decides at grant time).
 *
 * Plan credits last the plan month; on Growth what is left rolls over to the
 * next month (PLAN_CREDITS_ROLLOVER).
 *
 * KEEP IN SYNC with supabase/functions/_shared/topup.ts, which is what
 * create-checkout charges. src/lib/__tests__/topup.test.ts fails when the two
 * copies disagree.
 */
export const PAYG_NAME = 'Pay as you go';

export const TOPUP_STEP = 100;
export const TOPUP_MIN = 100;
export const TOPUP_MAX = 5000;
export const TOPUP_VALID_MONTHS = 12;

/** Extra credits on an account's first pay as you go purchase, in percent. */
export const TOPUP_FIRST_BONUS_PCT = 50;

/** The plan whose features pay as you go credits open. */
export const PAYG_UNLOCK_PLAN = 'pro';

/** Credits added on a first purchase of this size. */
export function topupFirstBonus(credits: number): number {
  return Math.floor((credits * TOPUP_FIRST_BONUS_PCT) / 100);
}

/** Cents per credit before any discount. */
export const TOPUP_BASE_CENTS = 49;

/** Discount bands: credits up to `upTo` (after the previous band) get `off` percent off. */
export const TOPUP_BANDS: ReadonlyArray<{ upTo: number; off: number }> = [
  { upTo: 500, off: 0 },
  { upTo: 1000, off: 15 },
  { upTo: 2500, off: 25 },
  { upTo: Number.POSITIVE_INFINITY, off: 35 },
];

/** A valid purchase size, or null. */
export function topupCredits(raw: unknown): number | null {
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n) || n < TOPUP_MIN || n > TOPUP_MAX || n % TOPUP_STEP !== 0) return null;
  return n;
}

/** Total in whole USD. Integer maths: cents x (100 - off) is in 1/100 of a cent. */
export function topupPriceUsd(credits: number): number {
  let units = 0;
  let from = 0;
  for (const band of TOPUP_BANDS) {
    if (credits <= from) break;
    const inBand = Math.min(credits, band.upTo) - from;
    units += inBand * TOPUP_BASE_CENTS * (100 - band.off);
    from = band.upTo;
  }
  return Math.floor((units + 5000) / 10000);
}

/** Average USD per credit for a purchase of this size, to the cent. */
export function topupPerCredit(credits: number): number {
  return credits > 0 ? Math.round((topupPriceUsd(credits) / credits) * 100) / 100 : TOPUP_BASE_CENTS / 100;
}

/** What the purchase saves against $0.49 a credit: dollars and whole percent. */
export function topupSaving(credits: number): { usd: number; pct: number } {
  const full = Math.round((credits * TOPUP_BASE_CENTS) / 100);
  const usd = Math.max(0, full - topupPriceUsd(credits));
  return { usd, pct: full > 0 ? Math.round((usd / full) * 100) : 0 };
}

/** "$45" or "$45.50". */
export function formatUsd(amount: number): string {
  return Number.isInteger(amount) ? `$${amount.toLocaleString('en-US')}` : `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Plan credits last the plan month; only these plans carry what is left into the next month. */
export const PLAN_CREDITS_ROLLOVER: ReadonlyArray<string> = ['growth'];

/**
 * Welcome credits on a first paid subscription, to use within 3 months:
 * 1 x the plan's monthly credits on a monthly plan, 2 x on a yearly plan
 * (grant_plan_signup_bonus reads the interval).
 */
export const WELCOME_MULTIPLIER = { monthly: 1, yearly: 2 } as const;
export const welcomeCredits = (credits: number, yearly: boolean) => credits * (yearly ? WELCOME_MULTIPLIER.yearly : WELCOME_MULTIPLIER.monthly);
/** Discount of paying yearly (8 months for 12). */
export const YEARLY_DISCOUNT_PCT = 33;
export const WELCOME_MONTHS = 3;
